import React, { useState, useEffect, useRef, useCallback } from "react";
import RoleSelector from "./RoleSelector";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  loginUser,
  getRoleList,
  refreshToken,
  clearError,
  clearRoleList,
} from "../../API_Service/Login_Slice";
import {
  buildMicrosoftLoginURL,
  getMSALConfig,
  saveMSALConfig,
} from "../../Common/msalConfig";
import ForgotPasswordModal from "./ForgotPasswordModal";
import apiRequest, { api } from "../../API_Service/AxiosConfiguration";
import { setCheckResult } from "../../API_Service/HappinessIndexSlice"; 

/* ─── Floating label input ─────────────────────────────────────────────────── */
const FloatingInput = ({
  label,
  type = "text",
  value,
  onChange,
  onBlur,
  error,
  autoFocus,
  maxLength,
}) => {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);
  const isPass = type === "password";
  const filled = value.length > 0;
  const floated = true;

  return (
    <div className="relative">
      <input
        autoFocus={autoFocus}
        type={isPass && show ? "text" : type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        placeholder=""
        className="peer w-full bg-white border rounded-xl py-4 px-5 text-sm font-medium text-slate-800 outline-none transition-all duration-200 placeholder-transparent"
        style={{
          paddingRight: isPass ? "3.25rem" : "1.25rem",
          borderColor: error ? "#ef4444" : filled ? "#08757C" : "#e2e8f0",
          boxShadow:
            filled && !error
              ? "0 0 0 3px rgba(8,117,124,0.08)"
              : error
                ? "0 0 0 3px rgba(239,68,68,0.08)"
                : "none",
        }}
      />

      {/* Floating label */}
      <label
        className="absolute pointer-events-none font-semibold uppercase tracking-widest transition-all duration-200"
        style={{
          left: "1.25rem",
          top: floated ? "-0.6rem" : "50%", // ✅ floated
          transform: floated ? "none" : "translateY(-50%)", // ✅ floated
          fontSize: floated ? "0.6rem" : "0.75rem", // ✅ floated
          color: floated ? "#08757C" : "#94a3b8",
          background: floated ? "white" : "transparent", // ✅ floated
          padding: floated ? "0 4px" : "0", // ✅ floated
        }}
      >
        {label}
      </label>

      {/* Password toggle */}
      {isPass && (
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((s) => !s)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#08757C] transition-colors"
        >
          <span className="material-symbols-outlined text-[20px]">
            {show ? "visibility_off" : "visibility"}
          </span>
        </button>
      )}

      {error && (
        <p className="flex items-center gap-1 text-red-500 text-xs mt-1.5 pl-1">
          <span className="material-symbols-outlined text-[13px]">error</span>
          {error}
        </p>
      )}
    </div>
  );
};

/* ─── Step dot ─────────────────────────────────────────────────────────────── */
const StepDot = ({ active, done, label }) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className="rounded-full transition-all duration-300"
      style={{
        width: active ? "10px" : "8px",
        height: active ? "10px" : "8px",
        background: done || active ? "#08757C" : "#e2e8f0",
        boxShadow: active ? "0 0 0 3px rgba(8,117,124,0.18)" : "none",
      }}
    />
    <span
      className="text-[9px] uppercase tracking-widest font-bold transition-colors duration-200"
      style={{ color: active || done ? "#08757C" : "#cbd5e1" }}
    >
      {label}
    </span>
  </div>
);

/* ─── Slide panel wrapper ──────────────────────────────────────────────────── */
const SlidePanel = ({ show, maxHeight = "320px", children }) => (
  <div
    style={{
      maxHeight: show ? maxHeight : "0px",
      opacity: show ? 1 : 0,
      overflow: "hidden",
      transition:
        "max-height 0.42s cubic-bezier(0.4,0,0.2,1), opacity 0.28s ease",
      pointerEvents: show ? "auto" : "none",
    }}
  >
    {children}
  </div>
);

/* ─── Main component ───────────────────────────────────────────────────────── */
const LoginPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
    remember: false,
    role: "",
  });
  const [fieldErrors, setFieldErrors] = useState({
    email: "",
    password: "",
    role: "",
  });
  const [forgotOpen, setForgotOpen] = useState(false);

  // SSO state
  const [ssoConfigured, setSsoConfigured] = useState(false);
  const [ssoDetected, setSsoDetected] = useState(false);
  const [ssoChecking, setSsoChecking] = useState(false);
  const domainCheckedRef = useRef("");
  const debounceRef = useRef(null);

  const { loading, error } = useSelector((s) => s.auth);
  const roles = useSelector((s) => s.auth.roleList);

  // Derive step: 1 = email entry, 2 = role pick, 3 = password
  const step = form.role
    ? 3
    : form.email.length >= 3 && roles.length > 0
      ? 2
      : 1;

  useEffect(() => {
    setSsoConfigured(!!getMSALConfig()?.clientId);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      dispatch(refreshToken()).then((result) => {
        if (refreshToken.fulfilled.match(result) && result.payload?.token)
          navigate("/user-management");
      });
    }
  }, []); // eslint-disable-line

  /* ── Domain SSO check ── */
  const checkDomain = useCallback(
    async (email) => {
      if (!email.includes("@")) return false;
      const domain = email.split("@")[1]?.toLowerCase();
      if (!domain || domain.length < 3 || domain === domainCheckedRef.current)
        return ssoDetected;

      domainCheckedRef.current = domain;
      setSsoChecking(true);
      setSsoDetected(false);
      let detected = false;
      try {
        const res = await api.get(
          `Auth/GetMSALConfigByDomain?domain=${encodeURIComponent(email)}`,
        );
        const d = res.data;
        if (d?.ClientId || d?.clientId) {
          saveMSALConfig({
            clientId: d.ClientId ?? d.clientId,
            authority: d.Authority ?? d.authority,
            redirectUri:
              d.RedirectUri ??
              d.redirectUri ??
              window.location.origin + "/ApiClient",
            allowedDomain: d.AllowedDomain ?? d.allowedDomain ?? domain,
          });
          setSsoDetected(true);
           detected = true;
        }
      } catch {
        /* domain not SSO-enabled */
        dispatch(getRoleList(email));
      } finally {
        setSsoChecking(false);
      }
      return detected;
    },
    [dispatch],
  );

  /* ── Email change — debounced 600ms ── */
  const handleEmailChange = (val) => {
    val = val.replace(/\s/g, "");
    dispatch(clearError());
    dispatch(clearRoleList());
    setForm((p) => ({ ...p, email: val, role: "" })); // reset role on email change
    setFieldErrors({ email: "", password: "", role: "" });

    domainCheckedRef.current = "";
    setSsoDetected(false);
    setSsoConfigured(false);

    // Reset SSO if @ removed
    if (!val.includes("@")) {
      domainCheckedRef.current = "";
      setSsoDetected(false);
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const isCompleteEmail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val);
      const isUsername = val.length >= 3 && !isCompleteEmail;

      if (isCompleteEmail) {
        checkDomain(val);
      } else if (isUsername) {
        dispatch(getRoleList(val));
      }
      // incomplete email → do nothing
    }, 1000);
  };

  const handleEmailBlur = () => {
    clearTimeout(debounceRef.current);

    const val = form.email;
    const isCompleteEmail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val);
    const isUsername = val.length >= 3 && !isCompleteEmail;

    if (isCompleteEmail) {
      checkDomain(val);
      if (!ssoDetected && roles.length === 0) dispatch(getRoleList(val));
    } else if (isUsername && roles.length === 0) {
      dispatch(getRoleList(val));
    }
  };

  const validate = () => {
    const e = { email: "", password: "", role: "" };
    let ok = true;
    if (!form.email.trim()) {
      e.email = "Email or username is required.";
      ok = false;
    }
    if (!form.password || form.password.trim().length === 0) {
      e.password = "Password cannot be empty or contain only spaces.";
      ok = false;
    }
    if (!form.role) {
      e.role = "Please select a role to continue.";
      ok = false;
    }
    setFieldErrors(e);
    return ok;
  };

const handleSubmit = async (ev) => {
  ev.preventDefault();
  if (!validate()) return;

  const res = await dispatch(loginUser(form));
  if (!loginUser.fulfilled.match(res)) return;

  const d = res.payload;
  if (!d?.isUserExists || !d?.isRoleExists) return; // error already set in slice

  const user = d.user;
 if (d.token) localStorage.setItem("token", d.token);
  if (user?.RoleCode?.toUpperCase() === "TO") {
    try {
      const checkRes = await apiRequest(
  "GET",
  `HappinessIndex/CheckToday?userId=${user.UserId}`
);
      const result = checkRes.data?.result ?? checkRes.data;

      if (result === "0" || result === "-1") {
        dispatch(setCheckResult(result));
        navigate("/happiness-index");
      } else {
        navigate("/lab-management/setup-lab-utilization");
      }
    } catch {
      navigate("/lab-management/setup-lab-utilization");
    }
  } else if (localStorage.getItem("isFromSmile") === "1") {
    navigate("/lab-management/lab-Booking");
  } else {
    navigate("/user-management");
  }
};

  const handleSSO = () => {
    const cfg = getMSALConfig();
    if (!cfg?.clientId) {
      alert("SSO not configured. Contact your Administrator.");
      return;
    }
    try {
      window.location.href = buildMicrosoftLoginURL();
    } catch (err) {
      alert(err.message || "Failed to initiate Microsoft login.");
    }
  };

  const CharCounter = ({ value, max }) => {
    const len = (value ?? "").length;
    if (len === 0) return null;
    const atLimit = len === max;
    return (
      <div
        className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-slate-400"}`}
      >
        <span>
          {len}/{max}
        </span>
        {atLimit && <span className="text-red-500">— limit reached</span>}
      </div>
    );
  };

  const showSSO = ssoConfigured || ssoDetected;
  const showRoles = form.email.length >= 3 && roles.length > 0;

  return (
    <section
      className="md:w-1/2 h-full overflow-y-auto flex items-start justify-center py-12 px-8 md:px-16"
      style={{
        background: "linear-gradient(160deg, #ffffff 60%, #f0fdfc 100%)",
         scrollbarGutter: "stable",
          overscrollBehavior: "contain",
      }}
    >
      <div className="w-full max-w-[420px]">
        {/* ── Header ── */}
        <div className="mb-9">
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 mb-5"
            style={{ background: "rgba(8,117,124,0.07)" }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#08757C] animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#08757C]">
              Secure Portal
            </span>
          </div>
          <h2 className="text-[2rem] font-black text-slate-900 leading-tight tracking-tight">
            Welcome back
          </h2>
          <p className="text-sm text-slate-400 mt-1.5">
            Enter your credentials to access the lab environment
          </p>
        </div>

        {/* ── Step indicator ── */}
        <div className="flex items-center gap-3 mb-9">
          <StepDot active={step === 1} done={step > 1} label="Identity" />
          <div
            className="flex-1 h-px transition-colors duration-300"
            style={{ background: step > 1 ? "#08757C" : "#e2e8f0" }}
          />
          <StepDot active={step === 2} done={step > 2} label="Role" />
          <div
            className="flex-1 h-px transition-colors duration-300"
            style={{ background: step > 2 ? "#08757C" : "#e2e8f0" }}
          />
          <StepDot active={step === 3} done={false} label="Access" />
        </div>

        <div className="space-y-5">
          {/* ── Step 1: Email / Username — always visible ── */}
          <FloatingInput
            label="Email or Username"
            type="text"
            value={form.email}
            onChange={(val) => {
              handleEmailChange(val.replace(/\s/g, ""));
            }}
            onBlur={handleEmailBlur}
            error={fieldErrors.email}
            autoFocus
            maxLength={50}
          />
          <CharCounter value={form.email} max={50} />
          {/* SSO checking dots */}
          <SlidePanel show={ssoChecking} maxHeight="40px">
            <div className="flex items-center gap-2 px-1 pb-1">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-[#08757C]"
                  style={{ animation: `lpBounce 1s ${i * 0.15}s infinite` }}
                />
              ))}
              <span className="text-xs text-[#08757C] font-medium">
                Checking domain SSO…
              </span>
            </div>
          </SlidePanel>

          {/* ── SSO button — slides in when domain matched ── */}
          <SlidePanel show={showSSO} maxHeight="130px">
            <div className="pb-1">
              <button
                type="button"
                onClick={handleSSO}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border-2 border-slate-200 bg-white transition-all duration-200 group"
                style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = "rgba(8,117,124,0.4)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = "#e2e8f0")
                }
              >
                <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
                  <rect width="10" height="10" fill="#F25022" />
                  <rect x="11" width="10" height="10" fill="#7FBA00" />
                  <rect y="11" width="10" height="10" fill="#00A4EF" />
                  <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
                </svg>
                <span className="text-sm font-semibold text-slate-700 group-hover:text-[#08757C] transition-colors">
                  Continue with Microsoft SSO
                </span>
              </button>

              <div className="flex items-center gap-3 mt-4">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest whitespace-nowrap">
                  or use password
                </span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
            </div>
          </SlidePanel>

          {/* ── Role selector — slides in after email is typed ── */}
          <SlidePanel show={showRoles} maxHeight="280px">
            <div className="pb-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
                Select your role
              </p>
              <RoleSelector
                selected={form.role}
                onChange={(role) => {
                  setForm((p) => ({ ...p, role }));
                  setFieldErrors({ email: "", password: "", role: "" });
                  dispatch(clearError());
                }}
              />
              {fieldErrors.role && (
                <p className="flex items-center gap-1 text-red-500 text-xs mt-2 pl-1">
                  <span className="material-symbols-outlined text-[13px]">
                    error
                  </span>
                  {fieldErrors.role}
                </p>
              )}
            </div>
          </SlidePanel>

          {/* ── Password — slides in once role is selected ── */}
          <SlidePanel show={!!form.role} maxHeight="240px">
            <div className="space-y-4 pb-1 pt-3">
              <FloatingInput
                label="Password"
                type="password"
                value={form.password}
                onChange={(val) => {
                  const password = val.replace(/\s/g, "");
                  setForm((p) => ({ ...p, password }));
                  setFieldErrors((p) => ({ ...p, password: "" }));
                  dispatch(clearError());
                }}
                error={fieldErrors.password}
                maxLength={500}
              />
              <CharCounter value={form.password} max={500} />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none group">
                  <div
                    className="w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-150 cursor-pointer"
                    style={{
                      borderColor: form.remember ? "#08757C" : "#cbd5e1",
                      background: form.remember ? "#08757C" : "white",
                    }}
                    onClick={() =>
                      setForm((p) => ({ ...p, remember: !p.remember }))
                    }
                  >
                    {form.remember && (
                      <span className="material-symbols-outlined text-white text-[11px]">
                        check
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 group-hover:text-slate-700 transition-colors">
                    Remember me
                  </span>
                </label>
              </div>
            </div>
          </SlidePanel>

          {/* ── Error banner ── */}
          {error && (
            <div className="flex items-center gap-2.5 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              <span className="material-symbols-outlined text-red-500 text-[17px]">
                warning
              </span>
              <p className="text-xs text-red-600 font-medium">{error}</p>
            </div>
          )}

          {/* ── Submit ── */}
          {!showSSO && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="relative w-full rounded-xl py-4 text-sm font-bold text-white tracking-wide overflow-hidden transition-opacity duration-200 disabled:opacity-60"
              style={{
                background: "linear-gradient(135deg, #08757C 0%, #0c9ea8 100%)",
                boxShadow: "0 4px 24px rgba(8,117,124,0.32)",
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="material-symbols-outlined text-[18px] animate-spin">
                    progress_activity
                  </span>
                  Authenticating…
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Sign In
                  <span className="material-symbols-outlined text-[18px]">
                    arrow_forward
                  </span>
                </span>
              )}
              {/* Shimmer overlay */}
              <span
                className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.18) 50%, transparent 65%)",
                }}
              />
            </button>
          )}
          {!showSSO && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-xs font-semibold text-[#08757C] hover:underline"
              >
                Forgot password?
              </button>
            </div>
          )}
        </div>

        {/* ── Footer note ── */}
        <p className="text-center text-[11px] text-slate-300 mt-8">
          Protected by enterprise-grade AES-256 encryption
        </p>
      </div>

      {/* Bounce animation for SSO checking dots */}
      <style>{`
        @keyframes lpBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40%           { transform: translateY(-5px); }
        }
      `}</style>

      <ForgotPasswordModal
        isOpen={forgotOpen}
        onClose={() => setForgotOpen(false)}
      />
    </section>
  );
};

export default LoginPanel;
